/**
 * Supabase API functions for direct database operations
 * Used for fetching products and order details
 *
 * NOTE: For creating orders, use the useCreateOrder hook from @/api/hooks
 * which properly calls the Edge Function API.
 */

import { config, getCheckoutApiUrl } from "./config";
import { getSessionToken } from "./utils";

/**
 * Get Supabase base URL from API base URL
 */
export function getSupabaseUrl(): string {
  const apiUrl = config.apiBaseUrl;
  try {
    const url = new URL(apiUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return "http://localhost:54321";
  }
}

/**
 * Get Supabase anon key from environment
 */
function getSupabaseAnonKey(): string {
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (envKey) {
    return envKey;
  }
  return "";
}

/**
 * Get headers for Supabase REST API requests
 */
export function getSupabaseHeaders() {
  return {
    apikey: getSupabaseAnonKey(),
    Authorization: `Bearer ${getSupabaseAnonKey()}`,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
}

/**
 * Pagination info from Amasty response
 */
// interface PageInfo {
//   current_page: number;
//   page_size: number;
//   total_pages: number;
// }

/**
 * Response from Amasty GraphQL
 */
// interface AmastyResponse {
//   data?: {
//     searchAmStoreLocations?: {
//       items: AmastyStoreLocation[];
//       total_count: number;
//       page_info?: PageInfo;
//     };
//   };
//   errors?: Array<{ message: string }>;
// }

/**
 * Shipping method condition criteria
 */
export interface ShippingConditionCriteria {
  type: string;
  amount: number;
}

/**
 * Shipping method condition
 */
export interface ShippingCondition {
  field: string;
  criteria: ShippingConditionCriteria;
  operator: string;
}

/**
 * Shipping method interface
 */
export interface ShippingMethod {
  id?: string;
  name: string;
  description: string;
  price_cents?: number;
  countries?: string[];
  provinces?: string[];
  conditions?: ShippingCondition[];
  type?: string;
  carrier_code?: string; // For Magento integration
  method_code?: string; // For Magento integration
  estimated_days?: number; // For marketplace brand shipping quotes
}

/**
 * FAQ interface
 */
export interface FAQ {
  question: string;
  answer: string;
}

/**
 * Retailer config interface
 */
export interface RetailerConfig {
  faq?: FAQ[] | null;
  shipping_methods?: ShippingMethod[] | null;
  enforce_shipping?: boolean | null;
}

/**
 * Retailer interface from database
 */
export interface Retailer {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  logo_dark_mode: string | null;
  logo_size: number | null;
  primary_color: string | null;
  config: RetailerConfig | null;
  isMarketplace?: boolean;
}

export interface IntegrationConfigResponse {
  shipping_config: unknown;
  base_url: string;
  type: string;
  bobgo: boolean;
}

export interface MarketplaceSessionResponse {
  parent_retailer_id: string;
  retailers: MarketplaceRetailer[];
}

export interface MarketplaceRetailer {
  retailer_id: string;
  name: string;
  logo_url?: string | null;
  enforce_shipping?: boolean;
  shipping_methods?: ShippingMethod[];
  integration_type?: string | null;
  bobgo?: boolean;
  base_url?: string | null;
  shipping_config?: unknown;
}

export interface PickupLocation {
  id: string;
  name: string;
  full_name: string;
  address_1: string;
  address_2: string | null;
  suburb: string;
  city: string;
}

/**
 * Fetch retailer config by retailer ID
 * @param retailerId - The retailer ID
 * @returns Retailer config object or null if not found
 */
export async function fetchRetailerConfig(retailerId: string, sessionToken = getSessionToken()): Promise<Retailer | null> {
  if (!retailerId) {
    throw new Error("Retailer ID is required to fetch retailer config");
  }

  const apiUrl = config.apiBaseUrl;
  const url = `${apiUrl}/retailers/search?id=${encodeURIComponent(retailerId)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: {
        "x-api-key": `${getSupabaseAnonKey()}`,
        "x-session-token": sessionToken ?? "",
      },
    });

    // 404 means retailer not found, return null
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch retailer config: ${response.status} ${response.statusText}. ${errorText}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch retailer config")) {
      throw error;
    }
    throw error;
  }

  const data = await response.json();
  if (data?.success && data?.data) {
    return data.data as Retailer;
  }
  return null;
}

/**
 * Fetch integration config by retailer ID
 * @param retailerId - The retailer ID
 * @returns Shipping config, type and baseUrl
 */
export async function fetchIntegrationConfig(
  retailerId: string,
  sessionToken = getSessionToken(),
): Promise<IntegrationConfigResponse | null> {
  if (!retailerId) {
    throw new Error("Retailer ID is required to fetch retailer config");
  }

  const apiUrl = config.apiBaseUrl;
  const url = `${apiUrl}/shipping-config/${retailerId}`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: {
        "x-api-key": `${getSupabaseAnonKey()}`,
        "x-session-token": sessionToken ?? "",
      },
    });

    // 404/NOT_FOUND is not an error - brand simply has no shipping config
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(
        `Failed to fetch shipping config: ${response.status} ${response.statusText}. ${errorText}`
      );
      throw error;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch shipping config")) {
      throw error;
    }
    throw error;
  }

  const data = await response.json();
  if (data?.data) {
    return {shipping_config: data.data.shipping_config, base_url: data.data.base_url, type: data.data.integration_type, bobgo: data.data.bobgo};
  }
  return null;
}

/**
 * Bob Go shipping rate
 */
export interface BobGoShippingMethod {
  service_name: string;
  total_price: number;
  description: string;
  min_delivery_date: string;
  max_delivery_date: string;
}

export interface CreateCumiCustomerRequest {
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface CreateCumiCustomerResponse {
  data: {
    success: boolean;
    customer_created: boolean;
    customer_id: string;
  };
}

export interface SearchCumiCustomerRequest {
  phone: string;
}

export interface SearchCumiCustomerResponse {
  success: boolean;
  endpoint: string;
  customer_found: boolean;
  customer_id: string;
  error?: string;
}

export interface VerifyCumiSkusRequest {
  retailerId: string;
  products: { productId: string; sku: string }[];
}

export interface VerifyCumiSkuResponse {
  sku: string;
  stock: number;
  price: number;
}

export interface CumiFareyeLocation {
  code: string;
  postal_code: string;
  suburb: string;
  city: string;
  province: string;
}

interface CumiFareyeResponse {
  success: boolean;
  data: CumiFareyeLocation[];
}

/**
 * Create a customer in Cumi Oracle.
 */
export async function createCumiCustomer(
  request: CreateCumiCustomerRequest,
  signal?: AbortSignal,
): Promise<CreateCumiCustomerResponse> {
  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/functions/v1/cumi/oracle/create`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "x-api-key": `${getSupabaseAnonKey()}`,
      "x-session-token": getSessionToken() ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create Cumi customer: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  const data = (await response.json()) as CreateCumiCustomerResponse;
  if (
    !data?.data ||
    typeof data.data.success !== "boolean" ||
    typeof data.data.customer_created !== "boolean" ||
    typeof data.data.customer_id !== "string"
  ) {
    throw new Error("Invalid Cumi customer response");
  }

  return data;
}


/**
 * Search Cumi Oracle for an existing customer by phone number.
 * A 404 with customer_found false means Oracle has no matching customer.
 */
export async function searchCumiCustomer(
  request: SearchCumiCustomerRequest,
  signal?: AbortSignal,
): Promise<SearchCumiCustomerResponse> {
  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/functions/v1/cumi/oracle/search`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "x-api-key": `${getSupabaseAnonKey()}`,
      "x-session-token": getSessionToken() ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ phone: request.phone }),
    signal,
  });

  if (response.status === 404) {
    let data: Partial<SearchCumiCustomerResponse> = {};
    try {
      data = (await response.json()) as Partial<SearchCumiCustomerResponse>;
    } catch {
      data = {};
    }
    if (data.customer_found === false) {
      return {
        success: false,
        endpoint: typeof data.endpoint === "string" ? data.endpoint : "oracle",
        customer_found: false,
        customer_id: "",
        error:
          typeof data.error === "string" ? data.error : "Customer not found",
      };
    }
    throw new Error(
      `Failed to search Cumi customer: 404 ${response.statusText}`,
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to search Cumi customer: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  const data = (await response.json()) as SearchCumiCustomerResponse;
  const customerId = typeof data?.customer_id === "string" ? data.customer_id.trim() : null;
  if (
    typeof data?.success !== "boolean" ||
    typeof data.customer_found !== "boolean" ||
    customerId === null ||
    (data.customer_found ? !customerId : customerId !== "")
  ) {
    throw new Error("Invalid Cumi customer search response");
  }

  return {
    ...data,
    customer_id: customerId,
  };
}

/**
 * Verify Cumi product SKUs and retrieve their current stock and price.
 */
export async function verifyCumiSkus(
  request: VerifyCumiSkusRequest,
  signal?: AbortSignal,
  sessionToken = getSessionToken(),
): Promise<VerifyCumiSkuResponse[]> {
  if (!request.retailerId) {
    throw new Error("Retailer ID is required to verify Cumi SKUs");
  }

  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/functions/v1/cumi/orders/verify-skus`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "x-api-key": `${getSupabaseAnonKey()}`,
      "x-session-token": sessionToken ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to verify Cumi SKUs: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  const responseData = (await response.json()) as { success: boolean; data: VerifyCumiSkuResponse[] };
  const data = responseData.data;
  if (
    !Array.isArray(data) ||
    data.some(
      (item) =>
        !item ||
        typeof item !== "object" ||
        typeof (item as VerifyCumiSkuResponse).sku !== "string" ||
        !Number.isFinite((item as VerifyCumiSkuResponse).stock) ||
        !Number.isFinite((item as VerifyCumiSkuResponse).price),
    )
  ) {
    throw new Error("Invalid Cumi SKU verification response");
  }

  return data;
}

/**
 * Find Cumi Fareye delivery locations matching a suburb.
 */
export async function fetchCumiFareyeLocations(
  suburb: string,
  signal?: AbortSignal,
): Promise<CumiFareyeLocation[]> {
  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/functions/v1/cumi/fareye`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "x-api-key": `${getSupabaseAnonKey()}`,
      "x-session-token": getSessionToken() ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ suburb }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch Cumi Fareye locations: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  const responseData = (await response.json()) as CumiFareyeResponse;
  if (
    !responseData ||
    responseData.success !== true ||
    !Array.isArray(responseData.data)
  ) {
    throw new Error("Invalid Cumi Fareye response");
  }

  return responseData.data;
}

/**
 * Fetch Bob Go shipping estimates
 * @param sessionId
 * @param formData - address form data
 * @param childRetailerId - optional child retailer id for marketplace carts
 * @returns Array of shipping methods
 */
export async function fetchBobgoShipping(
  sessionId: string,
  formData: object,
  childRetailerId?: string
): Promise<BobGoShippingMethod[]> {
  if (!sessionId) {
    throw new Error("Session ID is required to fetch Bob Go shipping estimates");
  }

  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/functions/v1/api/bobgo/rates`;

  const payload: Record<string, unknown> = {
    session_id: sessionId,
    shipping_address: formData,
  };
  if (childRetailerId) {
    payload.child_retailer_id = childRetailerId;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "x-api-key": `${getSupabaseAnonKey()}`,
        "x-session-token": getSessionToken() ?? "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch Bob Go shipping estimates: ${response.status} ${response.statusText}. ${errorText}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch Bob Go shipping estimates")) {
      throw error;
    }
    throw error;
  }

  const responseData = await response.json();
  const data = responseData?.data ?? {};

  const rates = Array.isArray(data?.rates)
    ? data.rates.map((rate: Record<string, unknown>) => ({
        service_name: rate.service_name ?? null,
        total_price: rate.total_price ?? null,
        description: rate.description ?? null,
        min_delivery_date: rate.min_delivery_date ?? null,
        max_delivery_date: rate.max_delivery_date ?? null,
      }))
    : [];

  return rates;
}

/**
 * Fetch marketplace session data
 * @param sessionId - The session ID
 * @returns Marketplace session response with child retailers
 */
export async function fetchMarketplaceSession(
  sessionId: string,
  sessionToken = getSessionToken(),
): Promise<MarketplaceSessionResponse | null> {
  if (!sessionId) {
    throw new Error("Session ID is required to fetch marketplace session");
  }

  const apiUrl = config.apiBaseUrl;
  const url = `${apiUrl}/marketplace/${encodeURIComponent(sessionId)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: {
        "x-api-key": `${getSupabaseAnonKey()}`,
        "x-session-token": sessionToken ?? "",
      },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch marketplace session: ${response.status} ${response.statusText}. ${errorText}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch marketplace session")) {
      throw error;
    }
    throw error;
  }

  const data = await response.json();
  if (data?.success && data?.data) {
    return data.data as MarketplaceSessionResponse;
  }
  return null;
}

/**
 * Fetch pickup locations
 */
export async function fetchPickupLocations(filters: {
  province: string;
  retailerId: string;
  type: string;
}): Promise<PickupLocation[]> {
  const url = getCheckoutApiUrl("/pickup-locations");

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "x-api-key": `${getSupabaseAnonKey()}`,
        "x-session-token": getSessionToken() ?? "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(filters),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch pickup locations: ${response.status} ${response.statusText}. ${errorText}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch pickup locations")) {
      throw error;
    }
    throw error;
  }

  const data = await response.json();
  return data.data as PickupLocation[];
}
